import { useEffect, useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { AdminView } from "@/lib/api/types";
import { ADMIN_ROLES } from "@/lib/auth/admin-roles";
import { toE164OrNull } from "@/lib/phone/phone";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { getRequiredFieldErrors } from "@/shared/components/forms/validation";
import { Banner, Button, Checkbox, Drawer, Field, Input, useToast } from "@/shared/rd";
import { useCreateAdminMutation } from "../services/useCreateAdminMutation";
import { useUpdateAdminMutation } from "../services/useUpdateAdminMutation";
import { ROLE_META, ROLE_ORDER } from "../lib/roles";
import { PasswordForm } from "./PasswordForm";

const FORM_ID = "admin-form";

type Props = {
  /** null: closed. "new": create. An admin: edit that one. */
  target: "new" | AdminView | null;
  /** The edit route named an admin the list does not have. */
  notFound?: boolean;
  onClose: () => void;
  /** Called after a create (the list closes the drawer's route). */
  onCreated: () => void;
};

/**
 * Create and edit an admin in a drawer over the list. Edit also holds the
 * password reset, in its own form (forms cannot nest), below the details.
 */
export function AdminEditor({ target, notFound, onClose, onCreated }: Props) {
  const t = useTranslations();
  const open = target !== null || Boolean(notFound);
  const admin = target && target !== "new" ? target : null;
  const isCreate = target === "new";
  const [submitting, setSubmitting] = useState(false);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      closeLabel={t(keys.common.close)}
      width={480}
      badge="lock"
      crumb={`${t(keys.admin.adminsTitle)} / ${admin ? admin.name : isCreate ? t("admin.adminsNewCrumb") : ""}`}
      title={admin ? admin.name : isCreate ? t(keys.admin.adminCreateAction) : t(keys.admin.adminsEditTitle)}
      sub={admin ? t("admin.adminsEditSub") : isCreate ? t("admin.adminsNewSub") : undefined}
      actions={
        notFound ? null : (
          <SubmitButton
            label={isCreate ? t(keys.common.create) : t(keys.common.update)}
            loadingLabel={isCreate ? t("admin.creatingAdmin") : t("admin.savingAdmin")}
            loading={submitting}
          />
        )
      }
    >
      {notFound ? (
        <p style={{ margin: 0, color: "var(--rd-text-secondary)" }}>{t(keys.errors.notFound)}</p>
      ) : (
        // Keyed so switching admins (or create → edit) starts from fresh state.
        <AdminFields key={admin?.id ?? "new"} admin={admin} onCreated={onCreated} onBusy={setSubmitting} />
      )}
    </Drawer>
  );
}

/** The drawer bar's submit, tied to the form by id (the form ignores a second submit while one is in flight). */
function SubmitButton({ label, loadingLabel, loading }: { label: string; loadingLabel: string; loading: boolean }) {
  return (
    <Button type="submit" form={FORM_ID} variant="primary" size="sm" loading={loading} loadingLabel={loadingLabel}>
      {label}
    </Button>
  );
}

function AdminFields({
  admin,
  onCreated,
  onBusy,
}: {
  admin: AdminView | null;
  onCreated: () => void;
  /** Mirrors the in-flight save to the drawer bar's submit button. */
  onBusy: (busy: boolean) => void;
}) {
  const t = useTranslations();
  const toast = useToast();
  const isCreate = admin === null;
  const { mutateAsync: createAdmin } = useCreateAdminMutation();
  const { mutateAsync: updateAdmin } = useUpdateAdminMutation();
  const [roles, setRoles] = useState<string[]>(() => (admin ? admin.roles : [ADMIN_ROLES.ADMIN]));
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmittingState] = useState(false);
  const setIsSubmitting = (busy: boolean) => {
    setIsSubmittingState(busy);
    onBusy(busy);
  };
  // A drawer closed mid-save must not leave the bar's button spinning.
  useEffect(() => () => onBusy(false), [onBusy]);

  const toggleRole = (role: string, on: boolean) =>
    setRoles((current) => (on ? [...new Set([...current, role])] : current.filter((r) => r !== role)));

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;
    setFieldErrors({});
    const formData = new FormData(event.currentTarget);
    const phone = String(formData.get("phone") ?? "");
    const orderedRoles = ROLE_ORDER.filter((role) => roles.includes(role));

    const errors = isCreate
      ? getRequiredFieldErrors(formData, ["name", "email", "phone", "password"], t(keys.errors.required))
      : {};
    if (orderedRoles.length === 0) errors.roles = t(keys.errors.required);
    if (Object.keys(errors).length > 0) {
      setError(null);
      setFieldErrors(errors);
      return;
    }
    // Create needs a phone; edit may leave it empty (unchanged).
    const normalizedPhone = phone.trim().length === 0 ? undefined : (toE164OrNull(phone) ?? undefined);
    if (phone.trim().length > 0 && !normalizedPhone) {
      setFieldErrors({ phone: t(keys.buyer.trackingValidationPhone) });
      return;
    }

    const name = String(formData.get("name") ?? "");
    setIsSubmitting(true);
    try {
      if (isCreate) {
        await createAdmin({
          name: String(formData.get("name") ?? ""),
          email: String(formData.get("email") ?? ""),
          phone: normalizedPhone ?? "",
          password: String(formData.get("password") ?? ""),
          roles: orderedRoles,
        });
      } else {
        await updateAdmin({
          adminId: admin.id,
          name: String(formData.get("name") ?? ""),
          email: String(formData.get("email") ?? ""),
          phone: normalizedPhone,
          roles: orderedRoles,
        });
      }
    } catch (caught) {
      setError(
        caught instanceof ApiJsonClientError ? caught.message || t(keys.errors.generic) : t(keys.errors.generic),
      );
      setIsSubmitting(false);
      return;
    }
    setIsSubmitting(false);
    setError(null);
    const shownName = name.trim() || admin?.name || "";
    if (isCreate) {
      toast.show({ message: t("admin.toastAdminCreatedNamed", { name: shownName }) });
      onCreated();
    } else {
      toast.show({ message: t("admin.toastAdminUpdatedNamed", { name: shownName }) });
    }
  };

  return (
    <>
      <form
        id={FORM_ID}
        onSubmit={handleSubmit}
        noValidate
        aria-busy={isSubmitting || undefined}
        style={{ display: "flex", flexDirection: "column", gap: 16 }}
      >
        {error ? (
          <Banner
            tone="error"
            title={isCreate ? t("admin.adminCreateFailed") : t("admin.adminSaveFailed")}
            actions={
              <Button type="submit" size="sm" loading={isSubmitting} loadingLabel={t(keys.shell.retrying)}>
                {t(keys.shell.retry)}
              </Button>
            }
          >
            {`${error} ${t("admin.nothingChanged")}`}
          </Banner>
        ) : null}
        <Field label={t(keys.common.name)} htmlFor="admin-name" error={fieldErrors.name}>
          <Input
            id="admin-name"
            name="name"
            defaultValue={admin?.name}
            placeholder={t(keys.placeholders.name)}
            invalid={Boolean(fieldErrors.name)}
            required={isCreate}
          />
        </Field>
        <Field label={t(keys.common.email)} htmlFor="admin-email" error={fieldErrors.email}>
          <Input
            id="admin-email"
            name="email"
            type="email"
            dir="ltr"
            defaultValue={admin?.email}
            placeholder={t(keys.placeholders.email)}
            invalid={Boolean(fieldErrors.email)}
            required={isCreate}
          />
        </Field>
        <Field label={t(keys.common.phone)} htmlFor="admin-phone" error={fieldErrors.phone}>
          <Input
            id="admin-phone"
            name="phone"
            type="tel"
            dir="ltr"
            autoComplete="tel"
            defaultValue={admin?.phone}
            placeholder={t(keys.placeholders.phone)}
            invalid={Boolean(fieldErrors.phone)}
            required={isCreate}
          />
        </Field>
        <Field label={t(keys.admin.rolesLabel)} help={t(keys.admin.rolesHelper)} error={fieldErrors.roles}>
          <div className="rd-checks" role="group" aria-label={t(keys.admin.rolesLabel)}>
            {ROLE_ORDER.map((role) => (
              <Checkbox
                key={role}
                label={t(ROLE_META[role].label)}
                checked={roles.includes(role)}
                onChange={(on) => toggleRole(role, on)}
              />
            ))}
          </div>
        </Field>
        {isCreate ? (
          <Field label={t(keys.common.password)} htmlFor="admin-password" error={fieldErrors.password}>
            <Input
              id="admin-password"
              name="password"
              type="password"
              autoComplete="new-password"
              placeholder={t(keys.placeholders.password)}
              invalid={Boolean(fieldErrors.password)}
              required
            />
          </Field>
        ) : null}
      </form>
      {admin ? <PasswordForm adminId={admin.id} adminName={admin.name} /> : null}
    </>
  );
}
